import type { FilaDeCartera } from '@/api/cartera.api';
import { PIPELINE_COLUMNS } from './crm';
import type { PrefCartera } from './pref-cartera';
import { CERRADO, pasosDe, plazoDe, posicionDe, presupuestoCorto, type Plazo } from './proceso';

/**
 * LA TABLA DE «CLIENTES» (tanda 6; «la tabla de Monday» que pidió Kervens): por qué se
 * agrupa, de qué color es cada grupo, qué dice su pie, qué columnas trae y cómo queda una
 * fila mientras el servidor confirma un cambio. PURO: lo pinta
 * `components/cartera/TablaDeClientes.tsx` y lo guarda `hooks/useEdicionDeClientes.ts`.
 */

export type Agrupar = PrefCartera['agrupar']; // 'etapa' | 'temperatura' | 'tipo' | 'asignado' | 'fuente' | 'nada'

export interface GrupoDeClientes {
  clave: string;
  etiqueta: string;
  /** Clase de Tailwind (un fondo): la franja del grupo y, en la etapa, también su píldora. */
  color: string;
}

export interface AgrupacionDeClientes {
  de: (fila: FilaDeCartera) => string;
  grupos: GrupoDeClientes[];
}

/** A quién se le puede dar un chat (lo arma `useAsignacion`). `yoId`: sin él no hay grupo «Yo» que acertar. */
export interface Asignables {
  agentes: Array<{ id: string; name: string }>;
  /** null: tu rol no ve al equipo. */
  personas: Array<{ id: string; nombre: string }> | null;
  yoId?: string | null;
}

/** Las opciones de «Agrupar por», en el orden en que se ofrecen. */
export const AGRUPAR: ReadonlyArray<{ id: Agrupar; etiqueta: string }> = [
  { id: 'etapa', etiqueta: 'Etapa' },
  { id: 'temperatura', etiqueta: 'Temperatura' },
  { id: 'tipo', etiqueta: 'Tipo' },
  { id: 'asignado', etiqueta: 'Asignado' },
  { id: 'fuente', etiqueta: 'Fuente' },
  { id: 'nada', etiqueta: 'Sin agrupar' },
];

// Del gris de «acaba de llegar» al ámbar de «a punto de firmar», y verde al cerrar. Todos
// aguantan letra blanca encima (contraste de 4.5 o más): la píldora de la etapa usa el mismo.
const COLORES_DE_ETAPA = [
  'bg-slate-500',
  'bg-sky-700',
  'bg-indigo-600',
  'bg-violet-600',
  'bg-fuchsia-700',
  'bg-rose-600',
  'bg-amber-700',
  'bg-teal-700',
  'bg-orange-700',
  'bg-blue-600',
];
const COLOR_DE_CERRADO = 'bg-emerald-700';
const SIN_COLOR = 'bg-slate-300 dark:bg-slate-600';

/** Un grupo por paso del proceso (el último, «Cerrado»), cada uno con su color: siempre el mismo para el mismo lugar en la fila. */
export function gruposDeEtapa(etapas: readonly string[]): GrupoDeClientes[] {
  return pasosDe(etapas).map((p, i) => ({
    clave: p.id,
    etiqueta: p.etiqueta,
    color: p.id === CERRADO ? COLOR_DE_CERRADO : COLORES_DE_ETAPA[i % COLORES_DE_ETAPA.length]!,
  }));
}

const TIPOS = [
  { clave: 'comprador', etiqueta: 'Comprador', color: 'bg-sky-700' },
  { clave: 'realtor', etiqueta: 'Realtor', color: 'bg-violet-600' },
  { clave: 'vendedor', etiqueta: 'Vendedor', color: 'bg-amber-700' },
  { clave: 'lujo', etiqueta: 'Lujo', color: 'bg-fuchsia-700' },
  { clave: 'otro', etiqueta: 'Otro', color: 'bg-slate-500' },
];
const COLORES_DE_AGENTE = ['bg-indigo-600', 'bg-fuchsia-700', 'bg-blue-600'];
const COLORES_DE_PERSONA = ['bg-sky-700', 'bg-teal-700', 'bg-rose-600', 'bg-orange-700'];

/**
 * En manos de quién está su chat: `sin-chat`, `yo`, `persona:<id>`, `equipo` (alguien que tu rol
 * no ve, o que ya no está), `espera` (se pausó la IA y nadie lo tomó), `agente:<id>` o `ia` (el
 * agente del canal). Mismo criterio que `asignadoA` (lib/cartera). Las tres que se pueden elegir
 * (`agente:`, `yo`, `persona:`) son el valor del desplegable de «Asignar».
 */
export function claveDeAsignado(fila: FilaDeCartera, { agentes, personas, yoId }: Asignables): string {
  const conv = fila.conversacion;
  if (!conv) return 'sin-chat';
  if (conv.personaId) {
    if (conv.personaId === yoId) return 'yo';
    return personas?.some((p) => p.id === conv.personaId) ? `persona:${conv.personaId}` : 'equipo';
  }
  if (conv.estado === 'WAITING_HUMAN') return 'espera';
  return agentes.some((a) => a.id === conv.agenteId) ? `agente:${conv.agenteId}` : 'ia';
}

/** Los grupos de «por asignado», en orden: la IA, cada agente, yo, cada persona del equipo y los que no tienen dueño claro. */
export function gruposDeAsignado({ agentes, personas }: Asignables): GrupoDeClientes[] {
  return [
    { clave: 'ia', etiqueta: 'La IA', color: 'bg-violet-600' },
    ...agentes.map((a, i) => ({ clave: `agente:${a.id}`, etiqueta: a.name, color: COLORES_DE_AGENTE[i % COLORES_DE_AGENTE.length]! })),
    { clave: 'yo', etiqueta: 'Yo', color: 'bg-emerald-700' },
    ...(personas ?? []).map((p, i) => ({ clave: `persona:${p.id}`, etiqueta: p.nombre, color: COLORES_DE_PERSONA[i % COLORES_DE_PERSONA.length]! })),
    { clave: 'equipo', etiqueta: 'Alguien del equipo', color: 'bg-sky-700' },
    { clave: 'espera', etiqueta: 'Espera a una persona', color: 'bg-amber-700' },
    { clave: 'sin-chat', etiqueta: 'Sin chat', color: SIN_COLOR },
  ];
}

/**
 * T14: un grupo por fuente, en el orden que vienen (`fuentesDe`: de la más grande a la más chica).
 * El color es el de su lugar: la misma fuente cambia de color si cambia de puesto, y está bien.
 */
export function gruposDeFuente(fuentes: readonly string[]): GrupoDeClientes[] {
  return fuentes.map((f, i) => ({ clave: f, etiqueta: f, color: COLORES_DE_ETAPA[(i + 1) % COLORES_DE_ETAPA.length]! }));
}

/** Los grupos y la función de reparto para la tabla, según por qué se agrupa. Con «nada», null. `fuentes`: las de la lista, para agrupar por fuente. */
export function agrupacionDe(agrupar: Agrupar, etapas: readonly string[], asignables: Asignables, fuentes: readonly string[] = []): AgrupacionDeClientes | null {
  switch (agrupar) {
    case 'etapa': {
      const grupos = gruposDeEtapa(etapas);
      return { grupos, de: (fila) => grupos[posicionDe(fila, etapas)]!.clave };
    }
    case 'temperatura': {
      const conocidas = new Set<string>(PIPELINE_COLUMNS.map((c) => c.status));
      return {
        grupos: [
          ...PIPELINE_COLUMNS.map((c) => ({ clave: c.status as string, etiqueta: c.label, color: c.dot })),
          // Tiene chat y nadie (ni la IA) le ha puesto temperatura todavía.
          { clave: 'sin-clasificar', etiqueta: 'Sin clasificar', color: SIN_COLOR },
          { clave: 'sin-chat', etiqueta: 'Sin chat', color: SIN_COLOR },
        ],
        de: (fila) => {
          if (!fila.conversacion) return 'sin-chat';
          const t = fila.conversacion.temperatura;
          return t && conocidas.has(t) ? t : 'sin-clasificar';
        },
      };
    }
    case 'tipo':
      return {
        grupos: [...TIPOS, { clave: 'sin-tipo', etiqueta: 'Sin tipo', color: SIN_COLOR }],
        de: (fila) => {
          const tipo = (fila.tipo ?? '').trim().toLowerCase();
          if (!tipo) return 'sin-tipo';
          // Un tipo que la ficha ya no ofrece («inversionista») es, para agrupar, un «otro».
          return TIPOS.some((t) => t.clave === tipo) ? tipo : 'otro';
        },
      };
    case 'asignado':
      return { grupos: gruposDeAsignado(asignables), de: (fila) => claveDeAsignado(fila, asignables) };
    case 'fuente':
      return { grupos: gruposDeFuente(fuentes), de: (fila) => fila.fuente };
    default:
      return null;
  }
}

/** El presupuesto como número, o null si está vacío o lo escribieron con letras («unos 400k»). */
function presupuestoDe(fila: FilaDeCartera): number | null {
  const texto = fila.ficha.presupuesto_usd?.trim();
  if (!texto) return null;
  const n = Number(texto.replace(/[$,\s]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** «US$1.2 M»: la suma de los presupuestos de esos clientes (los que tengan número), o null si ninguno lo tiene. */
export function sumaDePresupuestos(filas: FilaDeCartera[]): string | null {
  const suma = filas.reduce((total, f) => total + (presupuestoDe(f) ?? 0), 0);
  return suma > 0 ? presupuestoCorto(String(suma)) : null;
}

/** «3 clientes · US$1.2 M»: cuántos y la suma de sus presupuestos (los que tengan número). */
export function resumenDeGrupo(filas: FilaDeCartera[]): string {
  const cuantos = `${filas.length} ${filas.length === 1 ? 'cliente' : 'clientes'}`;
  const suma = sumaDePresupuestos(filas);
  return suma ? `${cuantos} · ${suma}` : cuantos;
}

/** «400000» → «400,000», como se escribe en Miami. Lo que no es un número se deja como está. */
export function conMiles(valor: string): string {
  const texto = valor.trim();
  const n = Number(texto);
  return texto !== '' && Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 2 }) : valor;
}

// Los campos de la ficha que la fila repite arriba, fuera de `ficha`.
const ESPEJOS: Record<string, 'tipo' | 'pais' | 'email' | 'nacionalidad' | 'etapa'> = {
  tipo_contacto: 'tipo',
  pais: 'pais',
  email: 'email',
  nacionalidad: 'nacionalidad',
  etapa: 'etapa',
};

/**
 * La fila con un campo de la ficha cambiado, para pintarla antes de que conteste el servidor:
 * toca `ficha[clave]` (vacío = se quita) y su espejo de arriba si lo tiene. El cumpleaños solo
 * se adelanta si viene como MM-DD; escrito de otra forma («14 de marzo») lo lee el servidor.
 */
export function conCampo(fila: FilaDeCartera, clave: string, valor: string): FilaDeCartera {
  const limpio = valor.trim();
  const ficha = { ...fila.ficha };
  if (limpio) ficha[clave] = limpio;
  else delete ficha[clave];
  const espejo = ESPEJOS[clave];
  return {
    ...fila,
    ficha,
    ...(espejo ? { [espejo]: limpio || null } : {}),
    ...(clave === 'cumpleanos' ? { cumpleanos: /^\d{2}-\d{2}$/.test(limpio) ? limpio : null } : {}),
  };
}

/** La fila con otra temperatura (sin chat no hay temperatura que cambiar: queda igual). */
export function conTemperatura(fila: FilaDeCartera, temperatura: string | null): FilaDeCartera {
  return fila.conversacion ? { ...fila, conversacion: { ...fila.conversacion, temperatura } } : fila;
}

/** La fila en otra etapa. «Cerrado» no es una etapa de la ficha: es la temperatura CERRADO de su chat. */
export function conEtapa(fila: FilaDeCartera, etapa: string): FilaDeCartera {
  return etapa === CERRADO ? conTemperatura(fila, 'CERRADO') : conCampo(fila, 'etapa', etapa);
}

// Lo que se pregunta y lo que se contesta al mover a un cliente de etapa: lo dicen igual la tabla, el kanban y la ficha.
export const CONFIRMAR_CIERRE = 'Marcarlo como cerrado crea los borradores de postventa. ¿Seguimos?';
export const SIN_CHAT_NO_CIERRA = 'Este cliente todavía no tiene chat: no se puede marcar como cerrado desde aquí.';
export const YA_CERRADO = 'Ya está cerrado. Para reabrirlo, cambia su temperatura en la ficha.';

/**
 * Lo que hay que preguntar antes de poner esa temperatura, o `null` si no hay nada que confirmar.
 * Cerrar crea los borradores de postventa: en la ficha se pregunta igual que en la tabla y el kanban.
 */
export function confirmarTemperatura(temperatura: string | null): string | null {
  return temperatura === 'CERRADO' ? CONFIRMAR_CIERRE : null;
}

export type AlSoltar =
  | { tipo: 'nada' }
  | { tipo: 'mover'; etapa: string }
  | { tipo: 'confirmar'; etapa: typeof CERRADO; pregunta: string }
  | { tipo: 'no'; porque: string };

/**
 * Qué pasa si esta tarjeta del kanban se suelta en esa columna (o se elige en su «Mover a…»):
 * nada (ya está ahí, o esa columna no existe), se mueve, hay que confirmarlo (cerrar crea los
 * borradores de postventa) o no se puede, y por qué (sin chat no hay cierre; uno ya cerrado se
 * reabre desde la ficha). Son las mismas reglas que aplica el servidor: aquí se dicen antes.
 */
export function alSoltar(fila: FilaDeCartera, destino: string, etapas: readonly string[]): AlSoltar {
  const pasos = pasosDe(etapas);
  const actual = pasos[posicionDe(fila, etapas)]!.id;
  if (destino === actual || !pasos.some((p) => p.id === destino)) return { tipo: 'nada' };
  if (actual === CERRADO) return { tipo: 'no', porque: YA_CERRADO };
  if (destino !== CERRADO) return { tipo: 'mover', etapa: destino };
  return fila.conversacion ? { tipo: 'confirmar', etapa: CERRADO, pregunta: CONFIRMAR_CIERRE } : { tipo: 'no', porque: SIN_CHAT_NO_CIERRA };
}

/** La fila con el chat ya en manos de quien dice el desplegable de «Asignar» (`agente:<id>`, `yo`, `persona:<id>`). */
export function conAsignacion(fila: FilaDeCartera, valor: string, yoId?: string | null): FilaDeCartera {
  const conv = fila.conversacion;
  if (!conv) return fila;
  if (valor.startsWith('agente:')) {
    // Vuelve a la IA: suelta a la persona y sale de la espera, como hace el servidor.
    const estado = conv.estado === 'WAITING_HUMAN' ? 'ACTIVE' : conv.estado;
    return { ...fila, conversacion: { ...conv, agenteId: valor.slice('agente:'.length), personaId: null, estado } };
  }
  const personaId = valor === 'yo' ? (yoId ?? conv.personaId) : valor.slice('persona:'.length);
  return { ...fila, conversacion: { ...conv, personaId } };
}

/**
 * Lo que dice la celda «Próxima acción»: qué toca hacer y, si tiene fecha, para cuándo (o cuánto
 * lleva vencido, en tono de alerta). Sale de `plazoDe`, sin el «Próximo:» que ya dice la columna.
 */
export function proximaDe(fila: FilaDeCartera, ahora: Date): { que: string; cuando: string | null; tono: Plazo['tono'] } {
  const plazo = plazoDe(fila, ahora);
  const texto = plazo.texto.replace(/^Próximo: /, '');
  const descripcion = fila.proximaAccion?.descripcion.trim();
  // El plazo habla de la próxima acción (y no del Zoom ni del cierre) cuando empieza así.
  const esDeLaAccion = /^(Próximo: |Vencido )/.test(plazo.texto);
  if (!descripcion || !esDeLaAccion) return { que: texto, cuando: null, tono: plazo.tono };
  // Con fecha, el plazo es el cuándo; sin fecha, el plazo era la descripción recortada.
  return { que: descripcion, cuando: fila.proximaAccion?.fecha ? texto : null, tono: plazo.tono };
}

/** Las columnas que arrancan a la vista, en su orden. Las de la ficha llevan la clave de su campo. */
export const COLUMNAS_DE_ENTRADA: string[] = ['cliente', 'etapa', 'temperatura', 'proxima', 'presupuesto_usd', 'zona', 'asignado', 'ultimo'];

/** El orden completo de las columnas conocidas: las de entrada y, detrás, las que arrancan ocultas. */
export const ORDEN_DE_COLUMNAS: string[] = [
  ...COLUMNAS_DE_ENTRADA,
  'proposito',
  'preapproval',
  'tipo_contacto',
  'tipo_propiedad',
  'habitaciones',
  'plazo_meses',
  'telefono',
  'email',
  'pais',
  'nacionalidad',
  'cumpleanos',
  'entro',
  // T14: de qué base salió. Oculta de entrada (las ocho del diseño no cambian); se pone en «Columnas».
  'fuente',
  'origen',
];

/** Las columnas que no salen de un campo de la ficha: existen en cualquier negocio. */
export const COLUMNAS_PROPIAS: ReadonlySet<string> = new Set(['cliente', 'etapa', 'temperatura', 'proxima', 'asignado', 'fuente', 'ultimo', 'telefono', 'entro', 'origen']);

/**
 * Las columnas de ESTE negocio, en orden: las propias y una por cada campo de su ficha. Un campo
 * que la lista no conoce (lo agregaron mañana en la configuración) va al final, y arranca oculto
 * porque no está en `COLUMNAS_DE_ENTRADA`. La etapa de la ficha no es una columna más: es «Etapa».
 */
export function ordenDeColumnas(clavesDeFicha: readonly string[]): string[] {
  const deFicha = clavesDeFicha.filter((k) => !COLUMNAS_PROPIAS.has(k));
  return [...ORDEN_DE_COLUMNAS.filter((id) => COLUMNAS_PROPIAS.has(id) || deFicha.includes(id)), ...deFicha.filter((k) => !ORDEN_DE_COLUMNAS.includes(k))];
}

const CLIENTE = 'cliente';

/**
 * Las columnas que se ven, en el orden en que se ven: las que el usuario guardó o, si no ha
 * tocado nada (`[]`), las de entrada. «Cliente» siempre está, y siempre es la primera.
 */
export function columnasALaVista(guardadas: readonly string[]): string[] {
  const ids = guardadas.length > 0 ? guardadas : COLUMNAS_DE_ENTRADA;
  return [CLIENTE, ...ids.filter((id) => id !== CLIENTE)];
}

/** Todas las columnas del negocio como se dibujan y se ofrecen: delante las que se ven, en SU orden; detrás, las ocultas. */
export function columnasEnOrden(todas: readonly string[], guardadas: readonly string[]): string[] {
  const vistas = columnasALaVista(guardadas).filter((id) => todas.includes(id));
  return [...vistas, ...todas.filter((id) => !vistas.includes(id))];
}

/** Mostrar u ocultar una columna. La que se muestra entra al final de las que se ven; «Cliente» no se oculta. */
export function alternarColumna(guardadas: readonly string[], id: string): string[] {
  const vistas = columnasALaVista(guardadas);
  if (id === CLIENTE) return vistas;
  return vistas.includes(id) ? vistas.filter((x) => x !== id) : [...vistas, id];
}

/**
 * Subir (-1) o bajar (1) de sitio una columna que se ve. Cambia de lugar con su vecina A LA VISTA:
 * lo guardado puede traer un campo que este negocio ya no tiene (`todas` dice cuáles existen), y
 * saltar sobre él no movería nada en pantalla. A «Cliente» nadie le pasa por delante.
 */
export function moverColumna(guardadas: readonly string[], id: string, hacia: -1 | 1, todas: readonly string[]): string[] {
  const vistas = columnasALaVista(guardadas);
  const desde = vistas.indexOf(id);
  if (desde <= 0) return vistas;
  let hasta = desde + hacia;
  while (hasta > 0 && hasta < vistas.length && !todas.includes(vistas[hasta]!)) hasta += hacia;
  if (hasta <= 0 || hasta >= vistas.length) return vistas;
  const movidas = [...vistas];
  [movidas[desde], movidas[hasta]] = [movidas[hasta]!, movidas[desde]!];
  return movidas;
}

export interface ResultadoDeLote {
  hechas: number;
  /** Las que no se pudieron, con el motivo que dio el servidor (o el del panel, si ni se intentó). */
  fallos: Array<{ nombre: string; motivo: string }>;
}

const NOMBRES_A_LA_VISTA = 3;

/**
 * Lo que se dice al terminar una acción en lote: cuántas sí y, de las que no, quiénes y por qué
 * (juntas las que comparten motivo; hasta tres nombres por motivo).
 */
export function resumenDeLote(r: ResultadoDeLote, hecho: (cuantas: number) => string): { tono: 'success' | 'warning' | 'error'; titulo: string; detalle?: string } {
  if (r.fallos.length === 0) return { tono: 'success', titulo: hecho(r.hechas) };
  const porMotivo = new Map<string, string[]>();
  for (const f of r.fallos) porMotivo.set(f.motivo, [...(porMotivo.get(f.motivo) ?? []), f.nombre]);
  const detalle = [...porMotivo]
    .map(([motivo, nombres]) => {
      const resto = nombres.length - NOMBRES_A_LA_VISTA;
      const quienes = nombres.slice(0, NOMBRES_A_LA_VISTA).join(', ') + (resto > 0 ? ` y ${resto} más` : '');
      return `${quienes}: ${motivo}`;
    })
    .join(' ');
  const noSePudo = r.fallos.length === 1 ? '1 no se pudo' : `${r.fallos.length} no se pudieron`;
  return r.hechas > 0 ? { tono: 'warning', titulo: `${hecho(r.hechas)} ${noSePudo}.`, detalle } : { tono: 'error', titulo: 'No se cambió ninguno.', detalle };
}
